import { FocusPoint } from '@lumapps/lumx/react';
import { WithComments, WithReactions } from '@lumapps/reactions/api/v2/types';
import { StructuredContent } from '@lumapps/structured-content/types';
import { UserReference } from '@lumapps/widget-base/types';
import { Layout } from '@lumapps/widget-layout/types/layout';

export type EventStatus = 'draft' | 'published' | 'unpublished' | 'deleted';

export enum RegistrationStatuses {
    going = 'going',
    notGoing = 'not_going',
    maybe = 'maybe',
    unanswered = 'unanswered',
}

export type RegistrationStatusTypes = 'going' | 'not_going' | 'maybe' | 'unanswered';

export interface Event extends WithReactions, WithComments {
    /** Event identifier. */
    id: string;
    /** Event structured content. */
    structuredContent: StructuredContent;
    /** Event author. */
    author: UserReference;
    /** Event creation date. */
    createdAt: string;
    /** Event start date. */
    startsAt: string;
    /** Event end date. */
    endsAt: string;
    /** Event location. */
    location: string;
    /** Event external url. */
    externalUrl: string;
    /** Label for Event external url. */
    externalUrlLabel?: { type: string };
    /** Whether the event author has decided to show the guests list to all or not. */
    isGuestsListVisible?: boolean;
    /** Event organizer */
    organizers: UserReference[];
    /** Whether the current user can edit/delete the event. */
    canEdit: boolean;
    /** Whether the user is registered to the event. */
    isUserRegistered: boolean;
    /** precise the status of the user registered registration. */
    registrationStatus: RegistrationStatusTypes;
    /** Whether the event is deleted or accessible. */
    status: EventStatus;
    /** Event shares. */
    shares: Array<{
        eventId: string;
        container: {
            containerType: 'SITE' | 'COMMUNITY';
            containerId: string;
        };
    }>;
    templateId: string;
    /** Whether the event has a limited number of guests */
    hasLimitedCapacity: boolean;
    /** The maximum number of users that can go to the event (have a registration status to 'going'). */
    capacity?: number;
    /** The minimum number of users that can go to the event (have a registration status to 'going'). */
    minCapacity?: number;
    /** The number of spots left for the event */
    remainingSpots?: number;
    /** Whether some users are invited to the event */
    hasGuests?: boolean;
}

/**
 * The minimal info needed to display an event in a page.
 * What is returned by /v2/organizations/{organizationId}/events/{eventId}/layout
 * cf: https://github.com/lumapps/core/blob/master/specs/generated/cms/contribution_api_v1.yaml
 */
export interface EventPage extends WithReactions, WithComments {
    /** Whether the user doing the request can edit the event */
    canEdit: boolean;
    /** Whether the user doing the request can delete the event */
    canDelete: boolean;
    /** Whether the user doing the request can invite other users to the event */
    canInvite: boolean;
    /** The event's title translated to the requested lang */
    title: string;
    /** Event page layout. */
    layout: Layout;
    /** Event id */
    id?: string;
    /** Event shares */
    shares: Event['shares'];
}

export interface Guest extends UserReference {
    registrationStatus?: RegistrationStatusTypes;
    profilePrimaryFieldValues?: string[];
}

export interface EventGuests {
    guests: Guest[];
    cursor?: string;
    more?: boolean;
}

export interface LayoutEvent extends WithReactions, WithComments {
    description: string;
    externalUrl: string;
    externalUrlLabel: {
        type: string;
    };
    id: string;
    isUserRegistered: boolean;
    registrationStatus: RegistrationStatusTypes;
    location: string;
    startsAt: string;
    endsAt: string;
    title: string;
    image: string;
    focalPoint: FocusPoint;
    status: EventStatus;
    organizers: UserReference[];
    /** The number of spots left for the event */
    remainingSpots?: number;
    hasLimitedCapacity: boolean;
}
